<template>
  <div class="asset-list">
    <v-data-table
      :headers="headers"
      :items="adminAssetList"
      sort-by="id"
      :sort-desc="true"
      :loading="tableLoader"
      :search="search"
      loading-text="Fetching Assets..."
      class="mb-12 elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Assets</v-toolbar-title>

          <v-spacer></v-spacer>

          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="mr-12"
          >
          </v-text-field>

          <!-- Create Asset Modal -->
          <v-dialog
            v-model="dialog"
            max-width="800px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                class="mb-2"
                v-bind="attrs"
                v-on="on"
                @click="clearAdminAsset"
              >
                New Asset
              </v-btn>
            </template>
            <AssetForm
              v-on:dialogClose="dialog = false"
            ></AssetForm>
          </v-dialog>

          <!-- Edit Asset Modal -->
          <v-dialog
            v-model="editDialog"
            max-width="800px"
          >
            <AssetForm
              v-on:dialogClose="editDialog = false"
            ></AssetForm>
          </v-dialog>

          <!-- Image Modal -->
          <v-dialog
            v-model="imageDialog"
            max-width="800px"
          >
            <AssetImageForm
              v-on:dialog-close="imageDialog = false"
            ></AssetImageForm>
          </v-dialog>

          <!-- Begin Sale Modal -->
          <v-dialog
            v-model="saleDialog"
            max-width="600px"
          >
            <AssetBeginSaleForm
              v-on:dialog-close="saleDialog = false"
            ></AssetBeginSaleForm>
          </v-dialog>

          <!-- Auction Purchase Reconciliation Modal -->
          <v-dialog
            v-model="purchaseDialog"
            max-width="600px"
          >
            <PurchaseForm
              v-on:dialogClose="purchaseDialog = false"
            ></PurchaseForm>
          </v-dialog>

          <!-- BuyItNow Purchase Reconciliation Modal -->
          <v-dialog
            v-model="buyItNowDialog"
            max-width="600px"
          >
            <BuyItNowReconcileForm
              v-on:dialogClose="buyItNowDialog = false"
            ></BuyItNowReconcileForm>
          </v-dialog>

          <!-- Offline Purchase Modal -->
          <v-dialog
              v-model="offlinePaymentDialog"
              max-width="600px"
          >
            <OfflinePaymentForm
                v-on:dialog-close="offlinePaymentDialog = false"
            ></OfflinePaymentForm>
          </v-dialog>

          <!-- Transfer Modal -->
          <v-dialog
              v-model="transferDialog"
              max-width="600px"
          >
            <TransferForm
                v-on:dialog-close="transferDialog = false"
            ></TransferForm>
          </v-dialog>
        </v-toolbar>
      </template>

      <!-- Custom Processing for Specific Items -->
      <template v-slot:item.title="{ item }">
        <router-link :to="`/asset/${item.id}`">
          {{ item.title }}
        </router-link>
      </template>

      <template v-slot:item.visible="{ item }">
        <v-simple-checkbox
          v-model="item.visible"
          disabled
        ></v-simple-checkbox>
      </template>

      <template v-slot:item.promoted="{ item }">
        <v-simple-checkbox
          v-model="item.promoted"
          disabled
        ></v-simple-checkbox>
      </template>

      <template v-slot:item.creationTimestamp="{ item }">
        {{ item.creationTimestamp | timestampToDate }}
      </template>

      <template v-slot:item.auctionStartTimestamp="{ item }">
        {{ item.auctionStartTimestamp | timestampToDate }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom v-if="item.salesState === 'DataEntry'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="saleForm(item.id)"
              v-bind="attrs"
              v-on="on"
              class="mr-1"
            >
              mdi-currency-usd
            </v-icon>
          </template>
          <span>Begin Sale</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="imageForm(item.id)"
              v-bind="attrs"
              v-on="on"
              class="mr-1"
            >
              mdi-file-image
            </v-icon>
          </template>
          <span>Images</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="editAsset(item.id)"
              v-bind="attrs"
              v-on="on"
            >
              mdi-pencil
            </v-icon>
          </template>
          <span>
            Edit
          </span>
        </v-tooltip>

        <v-tooltip
            bottom
            v-if="item.salesState === 'ForSale'"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
                small
                @click="offlinePaymentForm(item.id)"
                v-bind="attrs"
                v-on="on"
            >
              mdi-account-cash
            </v-icon>
          </template>
          <span>
            Record Offline Payment
          </span>
        </v-tooltip>


        <v-tooltip
            bottom
            v-if="item.salesState === 'ForSale' || item.salesState === 'SoldOut'"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
                small
                @click="transferForm(item.id)"
                v-bind="attrs"
                v-on="on"
            >
              mdi-transfer
            </v-icon>
          </template>
          <span>
            Transfer Shares
          </span>
        </v-tooltip>


        <v-tooltip bottom v-if="item.salesState === 'InAuction'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="purchaseForm(item.id)"
              v-bind="attrs"
              v-on="on"
            >
              mdi-cash-check
            </v-icon>
          </template>
          <span>
            Purchase Reconciliation
          </span>
        </v-tooltip>
        <v-tooltip bottom v-if="item.salesState === 'PendingPurchase'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="buyItNowForm(item.id)"
              v-bind="attrs"
              v-on="on"
            >
              mdi-cash-check
            </v-icon>
          </template>
          <span>
            Purchase Reconciliation
          </span>
        </v-tooltip>

        <!-- Suspend / Resume -->
        <v-tooltip bottom v-if="item.salesState === 'ForSale' && item.salesState !== 'Suspended'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="suspendSales(item.id)"
              v-bind="attrs"
              v-on="on"
            >
              mdi-pause-octagon
            </v-icon>
          </template>
          <span>
            Suspend Sales
          </span>
        </v-tooltip>
        <v-tooltip bottom v-if="item.salesState === 'Suspended'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="resumeSales(item.id)"
              v-bind="attrs"
              v-on="on"
            >
              mdi-play-circle
            </v-icon>
          </template>
          <span>
            Resume Sales
          </span>
        </v-tooltip>
      </template>

    </v-data-table>
  </div>
</template>

<script>
  import AssetForm from './AssetForm.vue'
  import PurchaseForm from './PurchaseForm.vue'
  import BuyItNowReconcileForm from './BuyItNowReconcileForm.vue'
  import AssetBeginSaleForm from './AssetBeginSaleForm.vue'
  import AssetImageForm from './AssetImageForm'
  import OfflinePaymentForm from './OfflinePaymentForm'
  import TransferForm from './TransferForm'
  import { mapGetters, mapActions } from 'vuex'

  export default {
    name: 'AssetList',

    components: {
      AssetForm,
      PurchaseForm,
      BuyItNowReconcileForm,
      AssetImageForm,
      AssetBeginSaleForm,
      OfflinePaymentForm,
      TransferForm
    },

    data: () => ({
      tableLoader: true,
      search: '',

      // Dialog show/hide
      dialog: false,
      editDialog: false,
      saleDialog: false,
      imageDialog: false,
      purchaseDialog: false,
      buyItNowDialog: false,
      offlinePaymentDialog: false,
      transferDialog: false,

      // Mapping datatable.
      headers: [
        {
          text: 'ID',
          value: 'id',
          align: 'start'
        },
         {
          text: 'Sales Model',
          value: 'salesModel'
        },
        {
          text: 'Title',
          value: 'title'
        },
        {
          text: 'Year',
          value: 'metadata.year'
        },
        {
          text: 'Make',
          value: 'metadata.make'
        },
        {
          text: 'Model',
          value: 'metadata.model'
        },
        {
          text: 'Consignor',
          value: 'consignor.name'
        },
        {
          text: 'Created',
          value: 'creationTimestamp'
        },
        {
          text: 'Auction Date',
          value: 'auctionStartTimestamp'
        },
        // {
        //   text: 'Auction Link',
        //   value: 'auctionLink',
        //   sortable: false
        // },
        {
          text: 'Sales Sate',
          value: 'salesState'
        },
        {
          text: 'Visible?',
          value: 'visible'
        },
        {
          text: 'Promoted?',
          value: 'promoted'
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'end',
          sortable: false
        }
      ]

    }),

    methods: {
      ...mapActions([
        'getAssets',
        'getAssetById',
        'clearAdminAsset',
        'suspendSales',
        'resumeSales'
      ]),

      editAsset: async function (editAssetId) {
        // Set this asset in state for the form.
        await this.getAssetById(editAssetId)

        // Open the dialogue.
        this.editDialog = true
      },

      imageForm: async function (editAssetId) {
        // Set this asset in state for the form.
        await this.getAssetById(editAssetId)

        // Open the dialogue.
        this.imageDialog = true
      },

      saleForm: async function (editAssetId) {
        // Set this asset in state for the form.
        await this.getAssetById(editAssetId)

        // Open the dialogue.
        this.saleDialog = true
      },

      purchaseForm: async function (editAssetId) {
        // Set this asset in state for the form.
        await this.getAssetById(editAssetId)

        // Open the dialogue.
        this.purchaseDialog = true
      },

      buyItNowForm: async function (editAssetId) {
        // Set the asset in state for the form.
        await this.getAssetById(editAssetId)

        this.buyItNowDialog = true
      },

      offlinePaymentForm: async function (editAssetId) {
        // Set the asset in state for the form.
        await this.getAssetById(editAssetId)

        this.offlinePaymentDialog = true
      },

      transferForm: async function (editAssetId) {
        // Set the asset in state for the form.
        await this.getAssetById(editAssetId)

        this.transferDialog = true
      }
    },

    computed: {
      ...mapGetters(['adminAssetList'])
    },

    created() {
      this.getAssets().then(() => {
        this.tableLoader = false
      })
    }
  }
</script>
